import React, { useState } from 'react';
import { supabase } from '../../lib/supabase';
import { 
  Search, 
  Globe, 
  CheckCircle, 
  XCircle, 
  Loader2,
  AlertCircle,
  Eye,
  Code,
  Database
} from 'lucide-react';

interface ResolutionResult {
  tenant_id: number;
  tenant_name: string;
  tenant_config: any;
  domain_config: any;
  merged_config: any;
}

export const DomainResolutionTester: React.FC = () => {
  const [domain, setDomain] = useState('');
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<ResolutionResult | null>(null);
  const [error, setError] = useState<string>('');
  const [showDetails, setShowDetails] = useState(false);

  const testResolution = async () => {
    if (!domain.trim()) {
      setError('Veuillez saisir un nom de domaine');
      return;
    }

    try {
      setLoading(true);
      setError('');
      setResult(null);

      const { data, error: rpcError } = await supabase
        .rpc('resolve_tenant_by_domain', { domain_input: domain.trim() });

      if (rpcError) throw rpcError;

      if (data && data.length > 0) {
        setResult(data[0]);
      } else {
        setError('Aucun tenant trouvé pour ce domaine');
      }
    } catch (err: any) {
      setError(`Erreur de résolution: ${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      testResolution();
    }
  };

  const formatConfig = (config: any) => {
    if (!config) return 'Aucune configuration';
    return JSON.stringify(config, null, 2);
  };

  const getCategoryInfo = (config: any) => {
    const category = config?.category || 'unknown';
    const categoryLabels = {
      boutique: 'Boutique E-commerce',
      presentation: 'Site de Présentation',
      elearning: 'Plateforme E-learning',
      vote: 'Système de Vote'
    };
    return categoryLabels[category as keyof typeof categoryLabels] || 'Catégorie inconnue';
  };

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-6">
      <div className="flex items-center mb-4">
        <Search className="w-5 h-5 mr-2 text-blue-600" />
        <h3 className="text-lg font-semibold text-gray-900">
          Testeur de Résolution de Domaine
        </h3>
      </div>

      <p className="text-sm text-gray-600 mb-4">
        Testez la résolution automatique d'un domaine vers son tenant et sa configuration
      </p>

      {/* Formulaire de test */}
      <div className="flex items-center space-x-3 mb-6">
        <div className="flex-1">
          <div className="relative">
            <Globe className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={domain}
              onChange={(e) => setDomain(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="exemple.com"
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            />
          </div>
        </div>
        <button
          onClick={testResolution}
          disabled={loading}
          className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? (
            <Loader2 className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <Search className="w-4 h-4 mr-2" />
          )}
          {loading ? 'Test...' : 'Tester'}
        </button>
      </div>

      {/* Messages d'erreur */}
      {error && (
        <div className="mb-4 bg-red-50 border border-red-200 rounded-lg p-4 flex items-center">
          <XCircle className="w-5 h-5 text-red-500 mr-2" />
          <span className="text-red-700">{error}</span>
        </div>
      )}

      {/* Résultats */}
      {result && (
        <div className="space-y-4">
          {/* Résumé du résultat */}
          <div className="bg-green-50 border border-green-200 rounded-lg p-4">
            <div className="flex items-center mb-2">
              <CheckCircle className="w-5 h-5 text-green-500 mr-2" />
              <span className="font-medium text-green-800">Résolution réussie</span>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
              <div>
                <span className="text-gray-600">Tenant ID:</span>
                <div className="font-medium text-gray-900">{result.tenant_id}</div>
              </div>
              <div>
                <span className="text-gray-600">Nom:</span>
                <div className="font-medium text-gray-900">{result.tenant_name}</div>
              </div>
              <div>
                <span className="text-gray-600">Catégorie:</span>
                <div className="font-medium text-gray-900">
                  {getCategoryInfo(result.merged_config)}
                </div>
              </div>
            </div>
          </div>

          {/* Configuration fusionnée (aperçu) */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
            <h4 className="font-medium text-gray-900 mb-2 flex items-center">
              <Database className="w-4 h-4 mr-2" />
              Configuration Fusionnée (Aperçu)
            </h4>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
              {result.merged_config?.colors && (
                <div>
                  <span className="text-gray-600">Couleurs:</span>
                  <div className="flex items-center space-x-2 mt-1">
                    {result.merged_config.colors.primary && (
                      <div className="flex items-center space-x-1">
                        <div 
                          className="w-4 h-4 rounded border"
                          style={{ backgroundColor: result.merged_config.colors.primary }}
                        />
                        <span className="text-xs">{result.merged_config.colors.primary}</span>
                      </div>
                    )}
                    {result.merged_config.colors.secondary && (
                      <div className="flex items-center space-x-1">
                        <div 
                          className="w-4 h-4 rounded border"
                          style={{ backgroundColor: result.merged_config.colors.secondary }}
                        />
                        <span className="text-xs">{result.merged_config.colors.secondary}</span>
                      </div>
                    )}
                  </div>
                </div>
              )}
              
              {result.merged_config?.features && (
                <div>
                  <span className="text-gray-600">Features actives:</span>
                  <div className="mt-1">
                    {Object.entries(result.merged_config.features)
                      .filter(([_, enabled]) => enabled)
                      .map(([feature, _]) => (
                        <span key={feature} className="inline-block bg-blue-100 text-blue-800 text-xs px-2 py-0.5 rounded mr-1 mb-1">
                          {feature}
                        </span>
                      ))}
                  </div>
                </div>
              )}

              {result.merged_config?.phone && (
                <div>
                  <span className="text-gray-600">Téléphone:</span>
                  <div className="font-medium">{result.merged_config.phone}</div>
                </div>
              )}

              {result.merged_config?.email && (
                <div>
                  <span className="text-gray-600">Email:</span>
                  <div className="font-medium">{result.merged_config.email}</div>
                </div>
              )}
            </div>
          </div>

          {/* Bouton pour voir les détails */}
          <div className="flex justify-center">
            <button
              onClick={() => setShowDetails(!showDetails)}
              className="flex items-center px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
            >
              {showDetails ? (
                <>
                  <Eye className="w-4 h-4 mr-2" />
                  Masquer les détails
                </>
              ) : (
                <>
                  <Code className="w-4 h-4 mr-2" />
                  Voir les détails JSON
                </>
              )}
            </button>
          </div>

          {/* Détails JSON */}
          {showDetails && (
            <div className="space-y-4">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Configuration Tenant (Base)</h4>
                <pre className="text-xs text-gray-700 bg-white p-3 rounded border overflow-x-auto">
                  {formatConfig(result.tenant_config)}
                </pre>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Configuration Domaine (Overrides)</h4>
                <pre className="text-xs text-gray-700 bg-white p-3 rounded border overflow-x-auto">
                  {formatConfig(result.domain_config)}
                </pre>
              </div>

              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <h4 className="font-medium text-gray-900 mb-2">Configuration Fusionnée (Finale)</h4>
                <pre className="text-xs text-gray-700 bg-white p-3 rounded border overflow-x-auto">
                  {formatConfig(result.merged_config)}
                </pre>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Exemples de domaines */}
      <div className="mt-6 pt-4 border-t border-gray-200">
        <h4 className="text-sm font-medium text-gray-900 mb-2">Domaines d'exemple à tester:</h4>
        <div className="flex flex-wrap gap-2">
          {['mode-femme.com', 'tech-store.com', 'consulting-pro.com', 'test-simple.com'].map((exampleDomain) => (
            <button
              key={exampleDomain}
              onClick={() => setDomain(exampleDomain)}
              className="text-xs px-2 py-1 bg-gray-100 text-gray-700 rounded hover:bg-gray-200 transition-colors"
            >
              {exampleDomain}
            </button>
          ))}
        </div>
      </div>
    </div>
  );
};
